import { fetchWrapRPC } from '../services/api';
import {
  type BatchResolvePermissionsResponse,
  QueryService_BatchResolvePermissions,
  type ResolvePermissionRequest,
} from './gen/ai/h2o/authorization/v1/query_api_pb';
import {
  type GetRoleBindingResponse,
  type ListRoleBindingsRequest,
  type ListRoleBindingsResponse,
  RoleBindingService_GetRoleBinding,
  RoleBindingService_ListRoleBindings,
} from './gen/ai/h2o/authorization/v1/role_binding_api_pb';
import {
  type GetGroupResponse,
  GroupService_GetGroup,
  GroupService_ListGroups,
  type ListGroupsRequest,
  type ListGroupsResponse,
} from './gen/ai/h2o/user/v1/group_api_pb';
import {
  type GetUserRequest,
  type GetUserResponse,
  type ListUsersRequest,
  type ListUsersResponse,
  UserService_GetUser,
  UserService_ListUsers,
} from './gen/ai/h2o/user/v1/user_api_pb';
import { type ListTagsRequest, type ListTagsResponse, TagService_ListTags } from './gen/ai/h2o/workspace/v1/tag_api_pb';
import {
  type AddTagsRequest,
  type AddTagsResponse,
  type ArchiveWorkspaceResponse,
  type CreateWorkspaceRequest,
  type CreateWorkspaceResponse,
  type DeleteWorkspaceResponse,
  type GetWorkspaceResponse,
  type ListAllWorkspacesRequest,
  type ListAllWorkspacesResponse,
  type ListWorkspacesRequest,
  type ListWorkspacesResponse,
  type RemoveTagsRequest,
  type RemoveTagsResponse,
  type UnarchiveWorkspaceResponse,
  type UndeleteWorkspaceResponse,
  type UpdateWorkspaceRequest,
  type UpdateWorkspaceResponse,
  WorkspaceService_AddTags,
  WorkspaceService_ArchiveWorkspace,
  WorkspaceService_CreateWorkspace,
  WorkspaceService_DeleteWorkspace,
  WorkspaceService_GetWorkspace,
  WorkspaceService_ListAllWorkspaces,
  WorkspaceService_ListWorkspaces,
  WorkspaceService_RemoveTags,
  WorkspaceService_UnarchiveWorkspace,
  WorkspaceService_UndeleteWorkspace,
  WorkspaceService_UpdateWorkspace,
} from './gen/ai/h2o/workspace/v1/workspace_api_pb';

// Groups

export const getGroup = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<GetGroupResponse> => {
  return fetchWrapRPC(GroupService_GetGroup, { basePath })({ name }, requestInit);
};

export const listGroups = (
  basePath: string,
  req: ListGroupsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListGroupsResponse> => {
  return fetchWrapRPC(GroupService_ListGroups, { basePath })(req, requestInit);
};

// Tags

export const listTags = (
  basePath: string,
  req: ListTagsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListTagsResponse> => {
  return fetchWrapRPC(TagService_ListTags, { basePath })(req, requestInit);
};

// Permissions

export type FixedResolvePermissionRequest = Pick<ResolvePermissionRequest, `context`> &
  Pick<Required<ResolvePermissionRequest>, `action` | `resource` | `subject`>;

export const batchResolvePermissions = (
  basePath: string,
  req: { requests: FixedResolvePermissionRequest[] },
  requestInit?: { signal?: AbortSignal }
): Promise<BatchResolvePermissionsResponse> => {
  return fetchWrapRPC(QueryService_BatchResolvePermissions, { basePath })(req, requestInit);
};

// Role Bindings

export const getRoleBinding = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<GetRoleBindingResponse> => {
  return fetchWrapRPC(RoleBindingService_GetRoleBinding, { basePath })({ name }, requestInit);
};

/**
 * These fields are supported in the ListRoleBindings request for filtering
 * @see https://github.com/h2oai/authz-server/blob/2adc6fdd841ea70eaca2b55617766003cee506f1/api/proto/ai/h2o/authorization/v1/role_binding_api.proto#L111-L112
 */
export type ListRoleBindingsSupportedFilterKeys = `resource` | `subject`;

export const listRoleBindings = (
  basePath: string,
  req: ListRoleBindingsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListRoleBindingsResponse> => {
  return fetchWrapRPC(RoleBindingService_ListRoleBindings, { basePath })(req, requestInit);
};

const resourcePrefix = `//workspaceserver/`;

export const roleBindingResourceCompile = (resourceName: string) => `${resourcePrefix}${resourceName}`;

export const roleBindingResourceParse = (resource: string) => resource.slice(resourcePrefix.length);

// Users

export const getUser = (
  basePath: string,
  req: GetUserRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<GetUserResponse> => {
  return fetchWrapRPC(UserService_GetUser, { basePath })(req, requestInit);
};

export const listUsers = (
  basePath: string,
  req: ListUsersRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListUsersResponse> => {
  return fetchWrapRPC(UserService_ListUsers, { basePath })(req, requestInit);
};

// Workspaces

export const createWorkspace = (
  basePath: string,
  req: CreateWorkspaceRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<CreateWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_CreateWorkspace, { basePath })(req, requestInit);
};

export const deleteWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_DeleteWorkspace, { basePath })({ name }, requestInit);
};

export const archiveWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<ArchiveWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_ArchiveWorkspace, { basePath })({ name }, requestInit);
};

export const getWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<GetWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_GetWorkspace, { basePath })({ name }, requestInit);
};

/**
 * These fields are supported in the ListWorkspace request for filtering and sorting
 * @see https://github.com/h2oai/authz-server/blob/25d7f9c3b778a7221db14ff04ff4aad07c794e80/api/proto/ai/h2o/workspace/v1/workspace_api.proto#L186-L199
 */
export type ListWorkspaceSupportedFilterOrderKeys =
  | `display_name`
  | `description`
  | `create_time`
  | `update_time`
  | `creator`
  | `updater`
  | `personal_workspace_subject`
  | `annotations`
  | `delete_time`
  | `deleter`
  | `purge_time`
  | `archiving_time`
  | `archiver`;

export const listWorkspaces = (
  basePath: string,
  req: ListWorkspacesRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListWorkspacesResponse> => {
  return fetchWrapRPC(WorkspaceService_ListWorkspaces, { basePath })(req, requestInit);
};

export const listAllWorkspaces = (
  basePath: string,
  req: ListAllWorkspacesRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListAllWorkspacesResponse> => {
  return fetchWrapRPC(WorkspaceService_ListAllWorkspaces, { basePath })(req, requestInit);
};

export const undeleteWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<UndeleteWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_UndeleteWorkspace, { basePath })({ name }, requestInit);
};

export const unarchiveWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<UnarchiveWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_UnarchiveWorkspace, { basePath })({ name }, requestInit);
};

export const updateWorkspace = (
  basePath: string,
  req: UpdateWorkspaceRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<UpdateWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_UpdateWorkspace, { basePath })(req, requestInit);
};

export const addTagsToWorkspace = (
  basePath: string,
  req: AddTagsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<AddTagsResponse> => {
  return fetchWrapRPC(WorkspaceService_AddTags, { basePath })(req, requestInit);
};

export const removeTagsFromWorkspace = (
  basePath: string,
  req: RemoveTagsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<RemoveTagsResponse> => {
  return fetchWrapRPC(WorkspaceService_RemoveTags, { basePath })(req, requestInit);
};
